import axios from 'axios';
import { getApiBaseUrl } from '../config/runtime';
import { authStorage } from './authStorage';
import { LocationService } from './LocationService';

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds for normal operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate instance for long-running AI operations
const aiLongRunningAPI = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 120000, // 2 minutes for AI processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens
const setupInterceptors = (instance) => {
  instance.interceptors.request.use(
    async (config) => {
      const token = await authStorage.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // console.log('Sending request with token:', token ? 'present' : 'missing');
      } else {
        console.warn('No auth token found in cookies');
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      // Response interceptor - no field mapping needed since backend returns correct fields
      return response;
    },
    (error) => {
      // Silence expected cancellation/timeout noise for aborted requests
      const code = error?.code || error?.response?.status;
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      const method = (error?.config?.method || '').toLowerCase();
      if (code === 'ECONNABORTED' || code === 'ERR_CANCELED' || error?.message === 'canceled' || error?.name === 'CanceledError') {
        // Do not spam console for expected aborts
        return Promise.reject(error);
      }
      // Treat 404 for project images as empty dataset to avoid noisy errors after deletes
      if (status === 404 && method === 'get' && url.includes('/projects/') && url.includes('/images')) {
        return Promise.resolve({
          data: { data: { images: [] } },
          status: 200,
          statusText: 'OK',
          headers: error?.response?.headers || {},
          config: error?.config,
          request: error?.request,
        });
      }
      // Suppress 404 logging for /mine endpoint - 404 is expected when no active session exists
      if (status === 404 && method === 'get' && url.includes('/mine')) {
        // Don't log - this is expected behavior, frontend handles it gracefully
        return Promise.reject(error);
      }
      console.error('API Error:', error.response?.status, error.response?.data);
      return Promise.reject(error);
    }
  );
};

setupInterceptors(api);
setupInterceptors(aiLongRunningAPI);

// Project APIs (using real /projects endpoints)
export const projectAPI = {
  getAll: (params = {}) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  getActive: () => api.get('/projects/active'),
  getPublic: (params = {}) => api.get('/projects/public', { params }),
  getFinished: (params = {}) => api.get('/projects/finished', { params }),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateStatus: (id, statusData) => api.patch(`/projects/${id}/status`, statusData),
  delete: (id) => api.delete(`/projects/${id}`),
  getOriginal: (id) => api.get(`/projects/${id}/original`),
  getImages: (id, params = {}) => api.get(`/projects/${id}/images`, { params }),
  join: (id) => api.post(`/projects/${id}/join`),
  leave: (id) => api.post(`/projects/${id}/leave`),
  getParticipants: (id) => api.get(`/projects/${id}/participants`),
  getActiveWebSocketUsers: (id) => api.get(`/projects/${id}/active-websocket-users`),
  getLeaderboard: (id) => api.get(`/projects/${id}/leaderboard`),
  getLive: (params = {}) => api.get('/projects/live', { params }),
  getUpcoming: (params = {}) => api.get('/projects/upcoming', { params }),
  getObjectiveContributors: (id) => api.get(`/projects/${id}/objectives/contributors`),
  broadcastShout: (id, message) => api.post(`/projects/${id}/shout`, { message }),
  createRematch: (id) => api.post(`/projects/${id}/rematch`),
  getRematchStatus: (id) => api.get(`/projects/${id}/rematch-status`),
  addObjective: (id, objectiveData) => api.post(`/projects/${id}/objectives`, objectiveData),
  batchUpdateObjectives: (id, payload) => {
    const body = Array.isArray(payload) ? { changes: payload } : payload;
    return api.post(`/projects/${id}/objectives/batch`, body);
  },
  removeObjective: (projectId, objectiveId) => api.delete(`/projects/${projectId}/objectives/${objectiveId}`),
  restart: (id) => api.post(`/projects/${id}/restart`),
  addCollaborator: (id, data) => api.post(`/projects/${id}/collaborators`, data),
  removeCollaborator: (id, userId) => api.delete(`/projects/${id}/collaborators/${userId}`),
  getEvents: (id, params = {}) => api.get(`/projects/${id}/events`, { params }),
  getReportChat: (id) => api.get(`/projects/${id}/report/chat`),
  sendReportMessage: (id, message) => api.post(`/projects/${id}/report/chat`, { content: message }),
};

// Tracking APIs
export const trackAPI = {
  start: (projectId, data = {}) => api.post(`/projects/${projectId}/tracks/start`, data),
  ingestPoints: (projectId, trackId, batch) => api.post(`/projects/${projectId}/tracks/${trackId}/points`, batch),
  finish: (projectId, trackId) => api.post(`/projects/${projectId}/tracks/${trackId}/finish`),
  getCurrent: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.chatId) searchParams.append('chat_id', params.chatId);
    const query = searchParams.toString();
    const suffix = query ? `?${query}` : '';
    return api.get(`/projects/${projectId}/tracks/current${suffix}`);
  },
  getLast: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.chatId) searchParams.append('chat_id', params.chatId);
    const query = searchParams.toString();
    const suffix = query ? `?${query}` : '';
    return api.get(`/projects/${projectId}/tracks/last${suffix}`);
  },
  getTrack: (trackId, params = { simplified: true }) => api.get(`/projects/tracks/${trackId}`, { params })
};

// ✅ NEW: Explore APIs for browsing pre-made projects/trails
export const exploreAPI = {
  // Get all explore projects with search and filtering
  getAll: (params = {}) => api.get('/explore', { params }),
  
  // Get explore projects near user's location
  getNearby: (lat, lng, radius = 25, limit = 20) => 
    api.get('/explore/nearby', { params: { lat, lng, radius, limit } }),
  
  // Get detailed information about a specific explore project
  getById: (id, userLocation = null) => {
    const params = userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : {};
    return api.get(`/explore/${id}`, { params });
  },
  
  // Clone an explore project to user's personal projects
  clone: (id, data = {}) => api.post(`/explore/${id}/clone`, data),
  
  // Create new explore project (requires editor permissions)
  create: (data) => api.post('/explore', data),
  
  // Update explore project (owner/admin only)
  update: (id, data) => api.put(`/explore/${id}`, data),
  
  // Delete explore project (owner/admin only)
  delete: (id) => api.delete(`/explore/${id}`),
  
  // Search explore projects with advanced filters
  search: (query, filters = {}) => 
    api.get('/explore', { params: { search: query, ...filters } }),

  // Search for a location by address or coordinates
  searchLocation: (query, searchType = 'address') => 
    api.post('/explore/search-location', { query, search_type: searchType }),
  
  // Get images for an explore project (template + all cloned project images)
  getImages: (id, params = {}) => api.get(`/explore/${id}/images`, { params })
};

// ✅ NEW: Reviews API
export const reviewAPI = {
  getSummary: (projectId) => api.get(`/reviews/projects/${projectId}/summary`),
  getList: (projectId, params = {}) => api.get(`/reviews/projects/${projectId}`, { params }),
  getMy: (projectId) => api.get(`/reviews/projects/${projectId}/my`),
  create: (projectId, data) => api.post(`/reviews/projects/${projectId}`, data),
  update: (reviewId, data) => api.patch(`/reviews/${reviewId}`, data),
  remove: (reviewId) => api.delete(`/reviews/${reviewId}`),
  getTags: () => api.get('/reviews/tags'),
  // Template aggregation endpoints
  getTemplateSummary: (templateId) => api.get(`/reviews/templates/${templateId}/summary`),
  getTemplateList: (templateId, params = {}) => api.get(`/reviews/templates/${templateId}`, { params }),
  // Session-level review endpoints
  getChatReview: (chatId) => api.get(`/reviews/chats/${chatId}`),
  getSessionReviews: (projectId) => api.get(`/reviews/projects/${projectId}/sessions`),
};

// ✅ NEW: Saved Lists APIs for managing user favorites
export const savedAPI = {
  // Get user's saved lists
  getLists: (params = {}) => api.get('/saved/lists', { params }),
  
  // Get specific saved list with items
  getList: (listId, includeItems = true) => 
    api.get(`/saved/lists/${listId}`, { params: { include_items: includeItems } }),
  
  // Create new saved list
  createList: (data) => api.post('/saved/lists', data),
  
  // Update saved list
  updateList: (listId, data) => api.put(`/saved/lists/${listId}`, data),
  
  // Delete saved list
  deleteList: (listId) => api.delete(`/saved/lists/${listId}`),
  
  // Add project to saved list
  addItem: (listId, projectId) => 
    api.post(`/saved/lists/${listId}/items`, { projectId }),
  
  // Remove project from saved list
  removeItem: (listId, projectId) => 
    api.delete(`/saved/lists/${listId}/items/${projectId}`),
  
  // Check if project is saved (and in which lists)
  getProjectStatus: (projectId) => 
    api.get(`/saved/project/${projectId}/status`),
  
  // Check saved status for multiple projects (bulk)
  getProjectsStatusBulk: (projectIds) => 
    api.get('/saved/project/bulk-status', { 
      params: { project_ids: projectIds.join(',') } 
    })
};

// Image upload for nature photos (simplified from document APIs)
export const imageAPI = {
  upload: (formData) => api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Nature AI Backend APIs - Simplified for Okio
export const aiAPI = {
  // System health check
  getStatus: () => api.get('/health'),
  
  // Nature-focused AI features (future)
  identifyPlant: (imageData) => {
    console.warn('Plant identification endpoint not implemented yet');
    return Promise.resolve({ species: null, confidence: 0 });
  },
};

// User profile APIs (nature-focused)
export const userAPI = {
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
};

// Admin APIs
export const adminAPI = {
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getStats: () => api.get('/admin/stats'),
};

// Authentication APIs
export const authAPI = {
  searchUsers: (query, limit = 10) => api.get('/auth/users/search', { params: { q: query, limit } }),
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  checkUsernameAvailability: (username) => api.get(`/auth/username/check/${username}`),
  getUserByUsername: (username) => api.get(`/auth/users/${username}`),
  getPublicUserProjects: (username, params = {}) => api.get(`/auth/users/${username}/projects`, { params }),
  refreshToken: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (profileData) => api.put('/auth/me', profileData),
  // OAuth APIs
  googleOAuthWeb: () => {
    // Redirect to backend OAuth endpoint
    window.location.href = `${api.defaults.baseURL}/auth/google`;
  },
  googleOAuthNative: (idToken, serverAuthCode) => api.post('/auth/google/native', { idToken, serverAuthCode }),
  appleOAuthNative: (identityToken, user) => api.post('/auth/apple/native', { identityToken, user }),
};

// Search APIs - Simplified for nature content
export const searchAPI = {
  projects: (query, filters = {}) => api.get('/search/projects', { params: { q: query, ...filters } }),
  nature: (query, projectId) => api.post('/search/nature', { query, projectId }),
};

// Chat API
export const chatAPI = {
  getProjectChats: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.search) searchParams.append('search', params.search);
    if (params.cursor && params.cursor.updatedAt && params.cursor.id) {
      // Pass cursor fields for seek pagination
      searchParams.append('cursorUpdatedAt', params.cursor.updatedAt);
      searchParams.append('cursorId', params.cursor.id);
    }

    return api.get(`/chats/project/${projectId}?${searchParams.toString()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  },
  getByProject: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.search) searchParams.append('search', params.search);
    
    return api.get(`/chats/project/${projectId}?${searchParams.toString()}`);
  },
  getById: (chatId) => api.get(`/chats/${chatId}`),
  getChat: (chatId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.include_messages !== undefined) {
      // Convert boolean to string for URLSearchParams (FastAPI will parse it back to boolean)
      searchParams.append('include_messages', String(params.include_messages));
    }
    if (params.messages_limit) {
      searchParams.append('messages_limit', String(params.messages_limit));
    }
    
    const url = `/chats/${chatId}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    return api.get(url);
  },
  getChatMessages: (chatId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    
    return api.get(`/chats/${chatId}/messages?${searchParams.toString()}`);
  },
  createChat: (data) => {
    return api.post('/chats', data).then(response => {
      return response;
    });
  },
  create: (data) => {
    return api.post('/chats', data).then(response => {
      return response;
    });
  },
  addMessage: (chatId, data) => api.post(`/chats/${chatId}/messages`, data),
  sendAIMessage: (chatId, content) => {
    console.log('🤖 Sending AI message to chat:', chatId, 'Content:', content);
    return api.post(`/chats/${chatId}/ai-message`, { content });
  },
  startNewSession: (projectId) => api.post(`/chats/project/${projectId}/session/new`),
  archiveCurrentSession: (projectId) => api.post(`/chats/project/${projectId}/session/archive`),
  streamLearnCard: (data) => {
    console.log('📚 Starting learn card streaming:', data);
    return api.post(`/chats/${data.messageId}/learn-card`, {
      cardId: data.cardId,
      cardTitle: data.cardTitle,
      cardSubtitle: data.cardSubtitle
    });
  },
  updateChat: (chatId, data) => api.patch(`/chats/${chatId}`, data),
  deleteChat: (chatId) => api.delete(`/chats/${chatId}`),
  getMineForProject: (projectId) => api.get(`/chats/project/${projectId}/mine`),
  // ThingsDB confirmation endpoints
  getDetectionCandidates: (detectionId) => api.get(`/thingsdb/detections/${detectionId}/candidates`),
  getDetectionStatus: (detectionId) => api.get(`/thingsdb/detections/${detectionId}/status`),
  confirmDetection: (detectionId, data) => api.post(`/thingsdb/detections/${detectionId}/confirm`, data),
  rejectDetection: (detectionId, data) => api.post(`/thingsdb/detections/${detectionId}/reject`, data),
  getThingTimeline: (thingId, projectId) => api.get(`/thingsdb/things/${thingId}/timeline`, { params: { project_id: projectId } })
};

// Objective matches API
export const objectiveMatchAPI = {
  confirm: (matchId) => api.post(`/objective-matches/${matchId}/confirm`),
  dismiss: (matchId) => api.post(`/objective-matches/${matchId}/dismiss`),
  get: (matchId) => api.get(`/objective-matches/${matchId}`),
  getStatuses: (ids) => api.get(`/objective-matches/status`, { params: { ids: Array.isArray(ids) ? ids.join(',') : ids } }),
  getProjectMatches: (projectId) => api.get(`/objective-matches/project/${projectId}`),
};

// Interactive cards state
export const interactiveCardsAPI = {
  markHandled: ({ messageId, cardId, state }) => api.post(`/interactive-cards/mark-handled`, { messageId, cardId, state }),
  explain: ({ messageId, cardId }) => api.post(`/interactive-cards/explain`, { messageId, cardId }),
  searchLocations: ({ searchIntent, userLocation }) => api.post(`/interactive-cards/location-search`, { search_intent: searchIntent, user_location: userLocation })
};

// ThingsDB API for Discover page (detections, observations, things)
export const thingsAPI = {
  // Get items (things, observations, or detections) with geo-filtering
  // layer: 'things' | 'observations' | 'detections'
  getItems: (params = {}) => api.get('/thingsdb/things', { params }),
  
  // Get a single detection by ID
  getDetection: (detectionId) => api.get(`/thingsdb/detections/${detectionId}`),
  
  // Get thing detail by ID
  getThing: (thingId) => api.get(`/thingsdb/things/${thingId}`),
  
  // Get observation detail by ID
  getObservation: (observationId) => api.get(`/thingsdb/observations/${observationId}`),
  
  // Get detections for a specific thing
  getThingDetections: (thingId, limit = 100) => api.get(`/thingsdb/things/${thingId}/detections`, { params: { limit } }),
  
  // Health check
  health: () => api.get('/thingsdb/health'),
};


// Helper function to handle API errors
export const handleAPIError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    return {
      status,
      message: data.message || 'An error occurred',
      details: data.details || null,
      errors: data.errors || null,
    };
  } else if (error.request) {
    return {
      status: 0,
      message: 'Network error - please check your connection',
      details: 'No response from server',
    };
  } else {
    return {
      status: 0,
      message: 'An unexpected error occurred',
      details: error.message,
    };
  }
};

// Query key factories for React Query
export const createQueryKey = {
  projects: () => ['projects'],
  project: (id) => ['projects', id],
  activeProject: () => ['projects', 'active'],
  liveProjects: () => ['projects', 'live'],
  upcomingProjects: () => ['projects', 'upcoming'],
  user: () => ['user'],
  health: () => ['health'],
  chats: (projectId) => ['project-chats', projectId],
  chat: (chatId) => ['chat', chatId],
  projectState: (projectId) => ['project-state', projectId],
  savedLists: () => ['saved-lists'],
  savedList: (id) => ['saved-lists', id],
  projectSavedStatus: (id) => ['project-saved-status', id],
  reviewsSummary: (projectId) => ['reviews-summary', projectId],
  reviewsList: (projectId, params) => ['reviews-list', projectId, params],
  myReview: (projectId) => ['my-review', projectId],
};


// Project state management
export const projectStateAPI = {
  // Get project state
  getProjectState: async (projectId) => {
    const response = await api.get(`/projects/${projectId}/state`);
    return response.data;
  },

  // Update project state (merges with existing state)
  updateProjectState: async (projectId, stateUpdate) => {
    const response = await api.post(`/projects/${projectId}/state`, stateUpdate);
    return response.data;
  },

  // Reset project state to empty
  resetProjectState: async (projectId) => {
    const response = await api.delete(`/projects/${projectId}/state`);
    return response.data;
  }
};

// Objective APIs
export const objectiveAPI = {
  getAll: (params = {}) => api.get('/objectives', { params }),
  getById: (id) => api.get(`/objectives/${id}`),
  create: (data) => api.post('/objectives', data),
  update: (id, data) => api.put(`/objectives/${id}`, data),
  delete: (id) => api.delete(`/objectives/${id}`),
};

// Export default instance for other uses
// ✅ NEW: Environment APIs for managing project environments
export const environmentAPI = {
  // Get all environments with search and filtering
  getAll: (params = {}) => api.get('/environments', { params }),
  
  // Get specific environment by ID
  getById: (id) => api.get(`/environments/${id}`),
  
  // Create new custom environment
  create: (data) => api.post('/environments', data),
  
  // Update environment (only if user created it)
  update: (id, data) => api.put(`/environments/${id}`, data),
  
  // Delete environment (only if user created it)
  delete: (id) => api.delete(`/environments/${id}`),
  
  // Search environments
  search: (query, params = {}) => 
    api.get('/environments', { params: { search: query, ...params } })
};


// ============ LOCATION UTILITIES ============

export const locationAPI = {
  // Get user's current location (browser API wrapper)
  getCurrentLocation: () => {
    return LocationService.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 })
      .then((pos) => ({ lat: pos.lat, lng: pos.lng }));
  }
};

export default api; 